import { useState } from 'react';
import { isoDateInZone } from '@cleartask/shared';
import { useTimeZone } from '../../session.js';
import { formatDuration, usePomodoroStats } from './usePomodoro.js';

const DIA_CURTO = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];

/**
 * 'YYYY-MM-DD' -> rotulo curto.
 *
 * Sem passar por `new Date('2026-07-31')`, que seria interpretada como UTC e
 * poderia render o dia anterior no fuso do dispositivo. Com `Date.UTC` a
 * leitura e a mesma em qualquer maquina.
 */
function rotuloDoDia(date: string): string {
  const [year, month, day] = date.split('-').map(Number);
  if (!year || !month || !day) return date;

  const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
  return `${DIA_CURTO[weekday]} ${String(day).padStart(2, '0')}`;
}

/**
 * Estatisticas de foco (spec 9.3).
 *
 * Barras proporcionais ao MAIOR dia do periodo, e nao a uma meta fixa: sem
 * meta declarada, uma escala absoluta so diria ao usuario que ele esta abaixo
 * de um numero que ninguem combinou.
 */
export function FocusStats() {
  const timeZone = useTimeZone();
  const [range, setRange] = useState<'week' | 'month'>('week');
  const { data, isPending, isError } = usePomodoroStats(range);

  const stats = data?.stats;
  const hoje = isoDateInZone(timeZone, Date.now());
  const doDia = stats?.byDay.find((d) => d.date === hoje);

  const maiorDia = Math.max(1, ...(stats?.byDay ?? []).map((d) => d.focusSeconds));

  return (
    <section className="flex w-full max-w-lg flex-col gap-4">
      <header className="flex items-center justify-between">
        <h2 className="font-display text-md font-semibold">Foco</h2>

        <div className="flex gap-1" role="tablist" aria-label="Período das estatísticas">
          {(['week', 'month'] as const).map((item) => (
            <button
              key={item}
              type="button"
              role="tab"
              aria-selected={item === range}
              onClick={() => setRange(item)}
              className={[
                'min-h-touch rounded-sm px-3 text-sm transition-colors duration-150',
                item === range
                  ? 'bg-brand-blue-soft font-medium text-brand-blue'
                  : 'text-text-mut hover:bg-surface-2',
              ].join(' ')}
            >
              {item === 'week' ? 'Semana' : 'Mês'}
            </button>
          ))}
        </div>
      </header>

      {isPending ? <div className="h-24 animate-pulse rounded bg-surface-2" /> : null}

      {isError ? (
        <p className="rounded bg-surface p-4 text-sm text-overdue" role="alert">
          Não foi possível carregar as estatísticas.
        </p>
      ) : null}

      {stats ? (
        <>
          <p className="rounded bg-surface p-4 text-sm shadow-card">
            <strong className="font-medium">Hoje:</strong>{' '}
            {formatDuration(doDia?.focusSeconds ?? 0)} de foco · {doDia?.pomos ?? 0} pomos
            <span className="text-text-mut">
              {' '}
              · {formatDuration(stats.totalFocusSeconds)} no período
            </span>
          </p>

          {stats.byDay.length === 0 ? (
            <p className="rounded bg-surface p-6 text-center text-sm shadow-card">
              Nenhuma sessão registrada ainda.{' '}
              <span className="text-text-mut">Comece um pomodoro na aba Pomo.</span>
            </p>
          ) : (
            <div className="flex flex-col gap-2 rounded bg-surface p-4 shadow-card">
              {stats.byDay.map((dia) => (
                <div key={dia.date} className="flex items-center gap-2 text-sm">
                  <span className="w-16 shrink-0 text-text-mut">{rotuloDoDia(dia.date)}</span>
                  <div className="h-3 flex-1 overflow-hidden rounded-sm bg-surface-2">
                    <div
                      className="h-full rounded-sm bg-brand-blue"
                      style={{ width: `${(dia.focusSeconds / maiorDia) * 100}%` }}
                    />
                  </div>
                  <span className="w-16 shrink-0 text-right tabular-nums text-text-mut">
                    {formatDuration(dia.focusSeconds)}
                  </span>
                </div>
              ))}
            </div>
          )}

          {stats.byTask.length > 0 ? (
            <div className="flex flex-col gap-1 rounded bg-surface p-4 shadow-card">
              <h3 className="pb-1 text-sm font-medium text-text-mut">Por tarefa</h3>
              {stats.byTask.map((tarefa) => (
                <div
                  key={tarefa.taskId ?? 'sem-tarefa'}
                  className="flex items-center justify-between gap-2 text-sm"
                >
                  <span className="truncate">
                    {tarefa.title ?? <span className="text-text-mut">Sem tarefa vinculada</span>}
                  </span>
                  <span className="shrink-0 tabular-nums text-text-mut">
                    {formatDuration(tarefa.focusSeconds)} · {tarefa.pomos} pomos
                  </span>
                </div>
              ))}
            </div>
          ) : null}
        </>
      ) : null}
    </section>
  );
}
